import React, { useState, useEffect, useCallback } from "react";
import Pagination from "../../../common/components/Pagination";
import AssignmentSection from "./AssignmentSection";
import { getFullName } from "../helpers/form.handler";
import DeleteModal from "../../../../../core/modals/deleteModal";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import { humanReadableDate } from "../../../../../helper/dateTimeHelper";

const ReportTypesList: React.FC<{
  reportTypes: any[];
  filteredReportTypes: any[];
  employees: any[];
  departments: any[];
  designations: any[];
  handleReportTypeDelete: any;
  handleReportTypeDuplicate: any;
  setEditData: any;
}> = ({
  reportTypes,
  filteredReportTypes,
  employees,
  departments,
  designations,
  handleReportTypeDelete,
  handleReportTypeDuplicate,
  setEditData,
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [duplicateItemId, setDuplicateItemId] = useState<any>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  const handleDuplicateModalOpen = useCallback((id: any) => {
    setDuplicateItemId(id);
    setIsModalOpen(true);
  }, []);

  // Calculate pagination values
  const totalItems = filteredReportTypes.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredReportTypes.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Reset to first page when filtered data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredReportTypes]);

  // Helper functions
  const getStatusText = (status: 0 | 1): "Active" | "Inactive" => {
    return status === 1 ? "Active" : "Inactive";
  };

  const getWeekDayNames = (days: number[] | null): string => {
    if (!days || days.length === 0) return "None";
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return days.map((day) => dayNames[day - 1]).join(", ");
  };

  const getFrequencyDisplay = (reportType: any) => {
    const {
      frequency_type,
      reports_per_week,
      weekly_interval,
      monthly_mode,
      reports_per_month,
      monthly_dates,
      monthly_interval,
    } = reportType;

    switch (frequency_type) {
      case "daily":
        return {
          label: "Daily",
          icon: "ti-calendar",
          color: "success",
          bgClass: "bg-success-subtle",
          textClass: "text-success",
          details: [`${getWeekDayNames(reportType.frequency_days)}`],
        };
      case "weekly":
        return {
          label: `Weekly`,
          icon: "ti-calendar-week",
          color: "info",
          bgClass: "bg-info-subtle",
          textClass: "text-info",
          details: [
            `${getWeekDayNames(reportType.frequency_days)}`,
            `1 report in ${weekly_interval} week(s)`,
          ],
        };
      case "monthly":
        const modeText =
          monthly_mode === "reportsCount"
            ? `${reports_per_month} report(s)/month`
            : `Dates: ${monthly_dates?.join(", ") || "None"}`;
        return {
          label: "Monthly",
          icon: "ti-calendar-month",
          color: "primary",
          bgClass: "bg-primary-subtle",
          textClass: "text-primary",
          details: [
            `${getWeekDayNames(reportType.frequency_days)}`,
            `1 report in ${monthly_interval} month(s)`,
          ],
        };
      default:
        return {
          label: "Never",
          icon: "ti-calendar-off",
          color: "secondary",
          bgClass: "bg-secondary-subtle",
          textClass: "text-secondary",
          details: "No frequency set",
        };
    }
  };

  const getDaysUntilStart = (startDate: string): number => {
    const start = new Date(startDate);
    const now = new Date();
    const diffTime = start.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getCreatorName = (userId: number): string => {
    const employee = employees.find((emp) => emp.id == userId);
    return employee
      ? getFullName(employee?.salutation, employee?.fname, employee?.lname)
      : `User ${userId}`;
  };

  const handleDelete = () => {
    handleReportTypeDelete(deleteItem);
  };

  const handleDuplicate = () => {
    handleReportTypeDuplicate(duplicateItemId);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <>
      {/* Horizontal Cards Layout */}
      <div className="report-types-container">
        {filteredReportTypes.length === 0 ? (
          <div className="card modern-empty-state border-0 text-center">
            <div className="card-body py-5">
              <div className="empty-state-icon mb-3">
                <i
                  className="ti ti-file-search text-muted"
                  style={{ fontSize: "4rem" }}></i>
              </div>
              <h4 className="empty-state-title mb-3">No Report Types Found</h4>
              <p className="empty-state-text">
                {reportTypes?.length > 0
                  ? "Try adjusting your search filters"
                  : "Create your first report type to get started"}
              </p>
              {reportTypes?.length === 0 && (
                <button
                  className="btn btn-primary btn-modern mt-3"
                  data-bs-toggle="modal"
                  data-bs-target="#ers_create_report_type_modal">
                  <i className="ti ti-plus me-2"></i>
                  Create Report Type
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {paginatedData.map((reportType) => {
              const frequency = getFrequencyDisplay(reportType);
              const statusText = getStatusText(reportType.status);
              const daysUntilStart = getDaysUntilStart(reportType.start_date);

              return (
                <div
                  key={reportType.id}
                  className="card modern-horizontal-card border-0 mb-3 shadow-sm">
                  <div className="card-body px-3 py-3 bg-white">
                    <div className="row g-3">
                      {/* Left Section - Main Info */}
                      <div className="col-lg-3">
                        <div className="report-main-info">
                          <div className="d-flex align-items-center mb-2">
                            <div className={`me-3`}>
                              <i
                                className={`ti ti-report-search ${frequency.textClass} fs-32`}></i>
                            </div>
                            <div>
                              <h5 className="report-title mb-1">
                                {reportType.name}
                              </h5>
                            </div>
                          </div>

                          <div className="frequency-details">
                            <p
                              className={`frequency-label ${frequency.textClass} mb-1`}>
                              <i className={`ti ${frequency.icon} me-1`}></i>
                              {frequency.label} Schedule
                            </p>
                            <p className="frequency-desc text-muted small mb-0">
                              {frequency.details[0]}
                            </p>
                            {frequency.details[1] && (
                              <p
                                className={`badge badge-sm badge-secondary-transparent small mb-0`}>
                                {frequency.details[1]}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Center Section - Enhanced Assignment Details */}
                      <div className="col-lg-6">
                        <div className="row g-3">
                          <AssignmentSection
                            title="Departments"
                            icon="ti-building-store"
                            colorClass="primary"
                            ids={reportType.report_departments}
                            dataSource={departments}
                            keyField="id"
                            nameField="name"
                            type="departments"
                          />

                          <AssignmentSection
                            title="Visible To"
                            icon="ti-users"
                            colorClass="info"
                            ids={reportType.shows_to}
                            dataSource={employees}
                            keyField="id"
                            nameField="full_name"
                            type="employees"
                          />

                          <AssignmentSection
                            title="Roles"
                            icon="ti-award"
                            colorClass="warning"
                            ids={reportType.report_designations}
                            dataSource={designations}
                            keyField="id"
                            nameField="name"
                            type="designations"
                          />
                        </div>
                      </div>

                      {/* Right Section - Dates */}
                      <div className="col-lg-2 align-items-center">
                        <div className="report-actions">
                          <div className="dates-info mb-2">
                            <div className="date-item mb-2 d-flex">
                              <div className="badge badge-sm badge-secondary-transparent me-1">
                                <i className="ti ti-calendar-event text-secondary fw-normal"></i>
                              </div>
                              <span className="text-muted">Start:</span>
                              <span className="fw-medium ms-1">
                                {humanReadableDate(reportType.start_date)}
                              </span>
                            </div>
                            {reportType.end_date && (
                              <div className="date-item mb-2">
                                <div className="badge badge-sm badge-secondary-transparent me-1">
                                  <i className="ti ti-calendar-off text-secondary fw-normal"></i>
                                </div>
                                <span className="text-muted">End:</span>
                                <span className="fw-medium ms-1">
                                  {humanReadableDate(reportType.end_date)}
                                </span>
                              </div>
                            )}
                            {daysUntilStart > 0 && (
                              <div className="countdown-badge d-flex mb-2">
                                <div className="badge badge-sm badge-secondary-transparent me-1">
                                  <i className="ti ti-clock text-secondary fw-normal"></i>
                                </div>
                                Starts in {daysUntilStart} days
                              </div>
                            )}
                            <div className="creator-info text-muted d-flex">
                              <div className="badge badge-sm badge-secondary-transparent me-1">
                                <i className="ti ti-user text-secondary fw-normal"></i>
                              </div>
                              <span className="text-muted me-1">By:</span>
                              {getCreatorName(reportType.created_by_user_id)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right Section - Actions*/}
                      <div className="col-lg-1 p-0">
                        <div className="report-actions gap-1">
                          <div
                            className={`badge badge-${
                              statusText === "Active" ? "success" : "danger"
                            } badge-md w-100`}>
                            <i
                              className={`ti ${
                                statusText === "Active"
                                  ? "ti-circle-check"
                                  : "ti-circle-x"
                              } me-1`}></i>
                            {statusText}
                          </div>
                          <button
                            className="btn btn-outline-info btn-sm w-100"
                            data-bs-toggle="modal"
                            data-bs-target="#ers_update_report_type_modal"
                            onClick={() => setEditData(reportType)}>
                            <i className="ti ti-edit"></i>
                            Edit
                          </button>
                          <button
                            className="btn btn-outline-success btn-sm w-100"
                            onClick={() =>
                              handleDuplicateModalOpen(reportType.id)
                            }>
                            <i className="ti ti-copy me-1"></i>
                            Duplicate
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm w-100"
                            data-bs-toggle="modal"
                            data-bs-target="#ers_report_type_delete_modal"
                            onClick={() => setDeleteItem(reportType.id)}>
                            <i className="ti ti-trash-x me-1"></i>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Updated Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              size="sm"
              showInfo={true}
            />
          </>
        )}
      </div>

      <DeleteModal
        modalId="ers_report_type_delete_modal"
        onDelete={handleDelete}
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
      />

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Duplicate Item"
        onConfirm={handleDuplicate}
        type="success"
      />
    </>
  );
};

export default ReportTypesList;
